/* eslint-disable complexity */
import React from 'react';
import styled from 'styled-components';
import { bool, func, number, object, shape, string } from 'prop-types';
import { compose, prop } from 'ramda';
import { useRouter } from '../../utils/router';

import {
  formatMessageDate,
  getDisplayWorkTime,
  splitText,
  pathWithParams,
  stripHtml,
  truncateText,
  historyPushWithParams
} from '../../utils/utils';
import { getTopSkills } from '../skill/utils';
import { getBadgeColor } from '../application/utils';

import Box from '../atoms/Box';
import Card from '../atoms/Card';
import Text from '../atoms/Text';
import Separator from '../atoms/Separator';
import SkillReadonly from '../atoms/SkillReadonly';
import Avatar from '../elements/Avatar';

import House from '../atoms/icons/House';
import Location from '../atoms/icons/Location';
import LocationPin from '../atoms/icons/LocationPin';
import Stopwatch from '../atoms/icons/Stopwatch';

import MatchingPercentage from '../molecules/MatchingPercentage';

import { MISSION, STATUS } from '../../constants';
import { useTranslate } from '../../translate/I18n';
import { useMoney } from '../../hooks/useMoney';
import companyRoutes from '../../routes/company';
import commonRoutes from '../../routes/common';
import ExternalLink from '../atoms/icons/ExternalLink';
import Button from '../atoms/Button';
import { useUserContext } from '../auth/hook/userContext';
import Link from '../atoms/Link';
import { getAppUrl } from '../../utils/config';
import MandatorySkillMessageMessage from '../skill/MandatorySkillMessageMessage';

const {
  APPLICATION: { HIRING, ONGOING, PENDING, APPLIED, REJECTED, ARCHIVED, HIRED }
} = STATUS;

const {
  PRICING_MODEL: { DAILY_RATE },
  FULL_REMOTE,
  PARTIAL_REMOTE,
  ON_SITE
} = MISSION;

const StyledSkillsWrapper = styled('div')(() => ({
  flexWrap: 'wrap',
  display: 'flex',
  whiteSpace: 'nowrap',
  position: 'relative',
  flex: 1,
  width: '100%',
  marginRight: 2,
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    width: '40px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)'
  }
}));

// @TODO: Deduplicate MissionCard components
const MissionCard = ({
                       currency,
                       matching,
                       title,
                       slugWithCompany,
                       description,
                       company,
                       missionLocation,
                       skills,
                       pricingModel,
                       dailyRateRange,
                       fixedPrice,
                       id,
                       weeklyPlanning,
                       workingStyle,
                       application,
                       publicationDate,
                       lastUpdateTime,
                       highlightedText,
                       numberOfRemoteDaysAllowed,
                       isLoggedIn,
                       displayMatching = true,
                       freelancerHasMissingMandatorySkill
                     }) => {
  const { t, i18n } = useTranslate(['skill', 'common', 'apply', 'mission', 'application', 'sectors']);
  const applied = [PENDING, APPLIED, ONGOING, HIRING].includes(application?.status);
  const rejected = [REJECTED, ARCHIVED].includes(application?.status);
  const hired = application?.status === HIRED;
  const badgeColor = getBadgeColor(application?.status);
  const money = useMoney();
  const user = useUserContext();
  const history = useRouter();

  const { sector, name: companyName, logoLink: companyLogoLink } = company || {};
  const linkToMission = `${getAppUrl()}${i18n.language !== 'en' ? `/${i18n.language}` : ''}` + (isLoggedIn ? '/m/' : '/missions/') + slugWithCompany;
  const hasMissionBeenUpdated = publicationDate !== lastUpdateTime;

  const getHighlightedText = (text, highlight) => {
    if (!highlight) {
      return text;
    }

    return (
      <span>
        {splitText(text, highlight).map((part, i) => (
          <span key={i} style={part.toLowerCase() === highlight.toLowerCase() ? { backgroundColor: '#35dbac' } : {}}>
            {part}
          </span>
        ))}
      </span>
    );
  };

  const companyProfileLink = `${i18n.language !== 'en' ? `/${i18n.language}` : ''}${pathWithParams(companyRoutes.companyProfile.to, { companyId: company.id })}`;
  const shouldDisplayMatching = isLoggedIn && displayMatching && !freelancerHasMissingMandatorySkill;
  const shouldDisplayMandatorySkillMissingMessage = isLoggedIn && displayMatching && freelancerHasMissingMandatorySkill;

  const viewMission = () => {
    const path = isLoggedIn ? commonRoutes.missionSlugWithCompanyLogged.to : commonRoutes.missionSlugWithCompany.to;

    historyPushWithParams(history, path, {
      missionSlugWithCompany: slugWithCompany
    });
  };

  const stopPropagation = e => {
    e.stopPropagation();
  };

  return (
    <Card
      display="block"
      mb={4}
      overflow="visible"
      pb={1}
      onClick={viewMission}
      borderLeft="2px solid"
      borderColor={badgeColor}
      data-cy="card-mission"
      data-cy-id={id}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mx={3} pt={1}>
        <Box display="flex">
          {!!companyLogoLink && (
            <Box mr={2} display="flex" alignItems="center">
              <Avatar link={companyLogoLink} />
            </Box>
          )}
          <Box>
            <Text styleName="pPTinyLight">
              <a href={companyProfileLink} onClick={stopPropagation}>
                <Text tag="span" mr={1}>
                  {getHighlightedText(companyName, highlightedText)}
                </Text>
                <ExternalLink data-cy="button-view-profile" fontSize="12px" color="main" />
              </a>
            </Text>
            <Text fontSize="10px" fontStyle="italic" lineHeight="10px" color="grey">
              {t('mission:publishedAt', { publicationDate: formatMessageDate(publicationDate, t, i18n.language) })}
              {hasMissionBeenUpdated &&
              t('mission:updatedAt', { updateDate: formatMessageDate(lastUpdateTime, t, i18n.language) })}
            </Text>
          </Box>
        </Box>
        <Box display="flex" minWidth="0">
          <Box display={{ _: 'none', md: 'flex' }} minWidth="0" mr={4}>
            {workingStyle !== FULL_REMOTE && (
              <Box display="flex" alignItems="center" minWidth="0">
                <Location fontSize="16px" color="greyDark" />
                <Text display="inline-flex" styleName="pPTinyLight" whiteSpace="nowrap" minWidth="0" ml={1}>
                  {missionLocation.getShortAddress()}
                </Text>
              </Box>
            )}

            <Box display="flex" alignItems="center" ml={4}>
              <Stopwatch fontSize="16px" color="greyDark" />
              <Text styleName="pPTinyLight" whiteSpace="nowrap" ml={1}>
                {getDisplayWorkTime(prop('workTime', weeklyPlanning), t)}
              </Text>
            </Box>

            <Box display="flex" alignItems="center" ml={4}>
              {workingStyle === ON_SITE ? (
                <>
                  <LocationPin fontSize="16px" color="greyDark" />
                  <Text display="inline-flex" styleName="pPTinyLight" whiteSpace="nowrap" minWidth="0" ml={1}>
                    {t('mission:onSiteOnly')}
                  </Text>
                </>
              ) : (
                <>
                  <House fontSize="16px" color="greyDark" />
                  <Text display="inline-flex" styleName="pPTinyLight" whiteSpace="nowrap" minWidth="0" ml={1}>
                    {workingStyle === PARTIAL_REMOTE &&
                    numberOfRemoteDaysAllowed > 0 &&
                    ` ${numberOfRemoteDaysAllowed} ${t('mission:daysPerWeekInRemote')}`}
                    {workingStyle === PARTIAL_REMOTE &&
                    (numberOfRemoteDaysAllowed === 0 || numberOfRemoteDaysAllowed === null) &&
                    t('mission:partialRemoteAvailable')}
                    {workingStyle === FULL_REMOTE && t('mission:remoteOnly')}
                  </Text>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <Text styleName="pH3">
              {pricingModel === DAILY_RATE
                ? money.formatRange(dailyRateRange, currency)
                : money.format(fixedPrice, currency)}
              <Text tag="span" styleName="pPTinyLight" whiteSpace="nowrap">
                {pricingModel === DAILY_RATE ? t('mission:dailyPay') : t('mission:fixedPrice')}
              </Text>
            </Text>
            <Text fontSize="10px" fontStyle="italic" lineHeight="10px" color="grey" textAlign="right">
              {pricingModel === DAILY_RATE ? t('mission:indicativeRate') : ''}
            </Text>
          </Box>
        </Box>
      </Box>
      <Separator my={1} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Text tag="h2" data-cy="mission-title" styleName="pH2" ml={3} onClick={() => null}>
          <a href={linkToMission} onClick={stopPropagation}>{getHighlightedText(truncateText(stripHtml(title), 50), highlightedText)}</a>
          <Text ml={3} tag="span" color="main" fontSize="14px" fontWeight="600">
            {sector && t(`sectors:${sector}`)}
          </Text>
        </Text>
        {applied && (
          <Text bg="rgba(0, 167, 255, .1)" fontWeight="700" color={badgeColor} px={1} mr={2} borderRadius="3">
            {t('common:youAppliedForThisMission')}
          </Text>
        )}
        {rejected && (
          <Text bg="rgba(197, 197, 197, .1)" fontWeight="700" color={badgeColor} px={1} mr={2} borderRadius="3">
            {t('common:youWereRejectedForThisMission')}
          </Text>
        )}
        {hired && (
          <Text bg="rgba(53, 219, 172, .1)" fontWeight="700" color={badgeColor} px={1} mr={2} borderRadius="3">
            {t('common:youWereHiredForThisMission')}
          </Text>
        )}
      </Box>
      <Text mx={3} mb={3} color="greyBluedarker">
        {getHighlightedText(truncateText(stripHtml(description), 200), highlightedText)}
      </Text>
      <Box display={{ _: 'block', md: 'none' }}>
        <Separator />
      </Box>
      <Box
        display="flex"
        flexDirection={{ _: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ _: 'flex-start', md: 'center' }}
        ml={2}
        mr={{ _: 2, md: 3 }}
        mb={1}>
        <StyledSkillsWrapper>
          {getTopSkills(skills).map((skill, index) => (
            <SkillReadonly
              {...skill}
              key={index}
              displayMandatorySkill={user.isFreelance()}
              tooltipMandatorySkill={t('skill:youAreMissingMandatorySkill')}
            />
          ))}
        </StyledSkillsWrapper>
        {shouldDisplayMandatorySkillMissingMessage && (
          <Box mt={{ _: 2, md: 0 }} ml={{ _: 0, md: 2 }}>
            <MandatorySkillMessageMessage
              text={t('mission:mandatorySkillMissing')}
              hoverText={t('skill:cannotMatchMissionMissingMandatorySkill')}
            />
          </Box>
        )}
        {shouldDisplayMatching && (
          <Box mt={{ _: 2, md: 0 }} ml={{ _: 0, md: 2 }} width={{ _: '100%', md: '242px' }}>
            <MatchingPercentage percentage={matching || 0} />
          </Box>
        )}
      </Box>
    </Card>
  );
};

MissionCard.propTypes = {
  id: string,
  title: string,
  slugWithCompany: string,
  company: shape({ sector: string, name: string, logoLink: string }),
  description: string,
  missionLocation: object,
  matching: number,
  skills: object,
  startDate: string,
  workingStyle: string,
  endDate: string,
  weeklyPlanning: object,
  isCompany: bool,
  pricingModel: string,
  dailyRateRange: shape({ min: number, max: number }),
  fixedPrice: number,
  currency: string,
  t: func,
  application: object,
  publicationDate: string,
  lastUpdateTime: string,
  highlightedText: string,
  numberOfRemoteDaysAllowed: number,
  isLoggedIn: bool,
  displayMatching: bool,
  freelancerHasMissingMandatorySkill: bool
};
export default MissionCard;
